"use client"

import { ArrowRightIcon } from "@heroicons/react/20/solid"
import Image from "next/image"
import Link from "next/link"
import { SubtleText } from "@ui/text"

import { CourseWithMeta, hasLive } from "../types"
import { formatDisplayDate } from "domains/utils/intl"

interface Props {
    courses: Record<string, CourseWithMeta<number>>
}

export function UpcomingCourseGrid({ courses }: Props) {
    const entries = Object.entries(courses)
        .filter(([, course]) => hasLive(course) && course.live?.utcTimestamp)
        .sort(([, a], [, b]) => a.live!.utcTimestamp! - b.live!.utcTimestamp!)
        .slice(0, 4)

    if (entries.length === 0) {
        return null
    }

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-xl font-semibold lg:text-2xl dark:text-gray-100">Upcoming Courses</h2>
                <Link
                    href="/academy/upcoming-courses"
                    className="text-primary-700 dark:text-primary-300 text-sm font-semibold underline-offset-4 hover:underline"
                >
                    Browse all
                    <ArrowRightIcon className="ml-1 inline size-4 align-middle" />
                </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
                {entries.map(([slug, course]) => (
                    <UpcomingCourseCard key={slug} slug={slug} course={course} />
                ))}
            </div>
        </section>
    )
}

function UpcomingCourseCard({ slug, course }: { slug: string; course: CourseWithMeta<number> }) {
    const live = course.live
    const startTs = live?.utcTimestamp ? new Date(live.utcTimestamp * 1000) : null
    const dateLabel = startTs ? formatDisplayDate(startTs) : "Date TBC"
    const durationLabel = typeof live?.durationMinutes === "number" ? `${live.durationMinutes}-min` : undefined
    const sessionLabel = live ? "Live session" : "Recording"
    const meta = [durationLabel, sessionLabel].filter(Boolean).join(" • ")
    const description = course.summary ?? live?.blurb ?? course.longDescription ?? ""
    const ticketUrl = live?.ticketTailor?.url ?? `/academy/videos/${slug}`
    const thumbnailUrl = course.thumbnail?.url ?? null

    return (
        <article className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-zinc-900">
            <div className="relative h-32 w-full overflow-hidden bg-gray-100 dark:bg-zinc-800">
                {thumbnailUrl ? (
                    <Image
                        src={thumbnailUrl}
                        alt={course.title}
                        fill
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        className="object-cover opacity-60"
                        priority={false}
                    />
                ) : null}
            </div>
            <div className="flex flex-1 flex-col gap-4 p-6">
                <div className="flex items-start justify-between text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs text-gray-700 dark:border-gray-700 dark:bg-zinc-800 dark:text-gray-200">
                        Upcoming
                    </span>
                    <span>{dateLabel}</span>
                </div>
                <div className="space-y-3">
                    <h3 className="text-lg leading-tight font-semibold text-gray-900 dark:text-gray-100">
                        {course.title}
                    </h3>
                    {description && (
                        <SubtleText className="text-sm text-gray-600 dark:text-gray-300">{description}</SubtleText>
                    )}
                </div>
                {meta && <SubtleText className="text-sm text-gray-500 dark:text-gray-400">{meta}</SubtleText>}
                <div className="mt-auto flex justify-end">
                    <Link
                        href={ticketUrl}
                        className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-900 dark:bg-white dark:text-black dark:hover:bg-gray-100"
                        target={ticketUrl.startsWith("http") ? "_blank" : undefined}
                        rel={ticketUrl.startsWith("http") ? "noopener noreferrer" : undefined}
                    >
                        Register
                    </Link>
                </div>
            </div>
        </article>
    )
}
